/**
 * TreeFilters.tsx — a READ-FAITHFUL render of the planner's QueryNode tree.
 *
 * Unlike SearchSummary (which renders the lossy flat SearchState and drops `when`/`scope`/structure),
 * this walks the tree directly so every leaf keeps its temporal scope, negation, similarity and
 * grouping. A "manager now / IC before" contrast reads — and is shown — as exactly that, instead of
 * collapsing into a contradictory pair of flat chips.
 *
 * This is render-only (no edit handlers yet) — the first half of moving the Talent Pool off flattening:
 * the tree becomes the source of truth and the chips become a faithful projection of it.
 */

import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { QueryNode, Group, Filter } from '@/lib/talentPool.api';
import { isGroup } from '@/lib/talentPool.api';
import { lbl, cap, SENIORITY_OPTS, STUDY_LEVEL_OPTS } from './tpEnums';
import { WhenToggle, ExcludeToggle } from './controls';
import { LeafValueEditor, EXPERIENCE_WHEN } from './leafFields';

// ── editing: remove a node by path, pruning any group left empty ──
function removeAtPath(node: QueryNode, path: number[]): QueryNode | null {
    if (!isGroup(node) || path.length === 0) return node;
    const [i, ...rest] = path;
    const child = node.children[i];
    if (!child) return node;
    let children: QueryNode[];
    if (rest.length === 0) {
        children = node.children.filter((_, x) => x !== i);
    } else {
        const next = removeAtPath(child, rest);
        children = node.children.map((c, x) => (x === i ? next : c)).filter((c): c is QueryNode => c != null);
    }
    // prune sub-groups that just became empty
    children = children.filter((c) => !(isGroup(c) && c.children.length === 0));
    return { ...node, children };
}

// ── editing: replace the leaf at a path with an edited version ──
function setAtPath(node: QueryNode, path: number[], next: Filter): QueryNode {
    if (!isGroup(node) || path.length === 0) return node;
    const [i, ...rest] = path;
    const children = node.children.map((c, x) => (x === i ? (rest.length === 0 ? next : setAtPath(c, rest, next)) : c));
    return { ...node, children };
}

type Remove = (path: number[]) => void;
type Update = (path: number[], next: Filter) => void;

// ── field → category (the section a leaf lands in) ──
const FIELD_CAT: Record<string, string> = {
    title: 'Role',
    seniority: 'Role',
    is_manager: 'Role',
    department: 'Role',
    responsibilities: 'Role',
    company: 'Company',
    company_size: 'Company',
    industry: 'Company',
    location_city: 'Location',
    location_country: 'Location',
    education_country: 'Education',
    field_of_study: 'Education',
    degree: 'Education',
    university: 'Education',
    study_level: 'Education',
    grad_year: 'Education',
    skill: 'Skills',
    certification: 'Skills',
    language: 'Languages',
    experience_years: 'Experience',
    avg_tenure: 'Experience',
    current_role: 'Experience',
    num_jobs: 'Experience',
    job_hopper: 'Experience',
    salary: 'Compensation',
    notice_period: 'Availability',
    work_authorized_country: 'Work auth',
    needs_visa_country: 'Work auth',
    name: 'Name',
    summary: 'Fit',
};

const CAT_ORDER = ['Role', 'Company', 'Skills', 'Location', 'Education', 'Languages', 'Experience', 'Compensation', 'Availability', 'Work auth', 'Fit', 'Name', 'Other'];

const FIELD_LABEL: Record<string, string> = {
    title: 'Title',
    seniority: 'Level',
    is_manager: '',
    department: 'Dept',
    responsibilities: 'Did',
    company: 'Company',
    company_size: 'Size',
    industry: 'Industry',
    location_city: 'City',
    location_country: 'Location',
    education_country: 'Studied in',
    field_of_study: 'Field',
    degree: 'Degree',
    university: 'School',
    study_level: 'Degree level',
    grad_year: 'Graduated',
    skill: 'Skill',
    certification: 'Cert',
    language: 'Speaks',
    experience_years: 'Experience',
    avg_tenure: 'Avg tenure',
    current_role: 'In current role',
    num_jobs: 'Roles held',
    job_hopper: '',
    salary: 'Expected pay',
    notice_period: 'Notice',
    work_authorized_country: 'Authorized in',
    needs_visa_country: 'Needs visa for',
    name: 'Name',
    summary: 'Overall',
};

function rangeText(f: Filter, unit = ''): string {
    if (f.field === 'grad_year') {
        if (f.gte != null && f.lte != null) return f.gte === f.lte ? `${f.gte}` : `${f.gte}–${f.lte}`;
        if (f.gte != null) return `after ${f.gte}`;
        if (f.lte != null) return `before ${f.lte}`;
    }
    if (f.field === 'salary') {
        const k = (n: number) => `$${Math.round(n / 1000)}k`;
        if (f.gte != null && f.lte != null) return `${k(f.gte)}–${k(f.lte)}`;
        if (f.gte != null) return `≥ ${k(f.gte)}`;
        if (f.lte != null) return `≤ ${k(f.lte)}`;
    }
    if (f.gte != null && f.lte != null) return `${f.gte}–${f.lte}${unit}`;
    if (f.gte != null) return `${f.gte}+${unit}`;
    if (f.lte != null) return `≤ ${f.lte}${unit}`;
    return '';
}

/** The human value text for a leaf (without qualifiers). */
function valueText(f: Filter): string {
    const vals = (f.values || []).filter(Boolean);
    switch (f.field) {
        case 'is_manager':
            return vals[0] === '1' ? 'People manager' : 'Individual contributor';
        case 'job_hopper':
            return vals[0] === '1' ? 'Frequent mover' : 'Stable tenure';
        case 'seniority':
            return lbl(vals[0] ?? '', SENIORITY_OPTS) + (f.exactLevels ? '' : '+');
        case 'study_level':
            return lbl(vals[0] ?? '', STUDY_LEVEL_OPTS) + (f.exactLevels ? '' : '+');
        case 'company_size':
            return vals.join(', ');
        case 'experience_years':
            return rangeText(f, ' yrs');
        case 'avg_tenure':
            return rangeText(f, ' yrs');
        case 'current_role':
            return rangeText(f, ' yrs');
        case 'num_jobs':
            return rangeText(f);
        case 'grad_year':
            return rangeText(f);
        case 'salary':
            return rangeText(f);
        case 'notice_period':
            return rangeText(f, ' days');
        case 'language':
            return vals[0] + (f.proficiency && f.proficiency !== 'any' ? ` · ${f.proficiency}` : '');
        default:
            return vals.length > 1 ? `${vals[0]} +${vals.length - 1}` : vals[0] ?? '…';
    }
}

const isRange = (f: Filter) => f.gte != null || f.lte != null;

/** Inline popover editor for a single leaf — values / when / similarity / range / exclude, by field kind. */
function LeafEditor({ f, onChange, onClose }: { f: Filter; onChange: (next: Filter) => void; onClose: () => void }) {
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [onClose]);

    const label = FIELD_LABEL[f.field] || cap(f.field);

    return (
        <div ref={ref} className="absolute left-0 top-full z-40 mt-1 w-[300px] rounded-xl border border-zinc-200 bg-white p-3 text-zinc-700 shadow-xl" onMouseDown={(e) => e.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{label}</span>
                {!isRange(f) && <ExcludeToggle on={!!f.negate} onChange={(v) => onChange({ ...f, negate: v || undefined })} />}
            </div>

            {EXPERIENCE_WHEN.has(f.field) && (
                <div className="mb-2">
                    <WhenToggle value={f.when} onChange={(w) => onChange({ ...f, when: w === 'any' ? undefined : w })} />
                </div>
            )}

            <LeafValueEditor f={f} onChange={onChange} />

            <div className="mt-2.5 flex justify-end">
                <button type="button" onClick={onClose} className="rounded-md bg-[#0D1830] px-3 py-1 text-[12px] font-medium text-white hover:bg-[#1C2B4D]">
                    Done
                </button>
            </div>
        </div>
    );
}

/** A Now/Was pill for the temporal `when` scope. */
const whenPill = (when?: string) =>
    when === 'past' ? { text: 'Was', cls: 'bg-amber-100 text-amber-800' } : when === 'current' ? { text: 'Now', cls: 'bg-emerald-100 text-emerald-700' } : null;

/**
 * Where this value sits on the exact → semantic spectrum, legible without opening the editor.
 * Mirrors the engine's compilation: 0 = exact keyword; <0.3 = word match + typo tolerance (no semantic
 * leg); ≥0.3 = an embedding leg is blended in and widens with the slider. Click-through opens the editor.
 */
const spectrumBadge = (f: Filter) => {
    const s = f.similarity;
    if (s == null || isRange(f) || f.field === 'language') return null;
    if (s === 0) return { text: 'exact', cls: 'bg-zinc-100 text-zinc-500', tip: 'Exact match only' };
    if (s < 0.3) return { text: '~word', cls: 'bg-sky-50 text-sky-700', tip: `Word match with typo tolerance (${s.toFixed(2)}) — no semantic widening` };
    return { text: `≈${s.toFixed(2)}`, cls: 'bg-violet-50 text-violet-700', tip: `Semantically widened to similar meanings (${s.toFixed(2)}) — click to adjust` };
};

/** A single leaf rendered as a faithful chip with its qualifier badges; click to edit when editable. */
function LeafChip({ f, path, remove, update, suppressWhen }: { f: Filter; path: number[]; remove?: Remove; update?: Update; suppressWhen?: boolean }) {
    const [open, setOpen] = useState(false);
    const label = FIELD_LABEL[f.field] ?? cap(f.field);
    const value = valueText(f);
    // `when` is the ROLE's temporal scope — shown once on the scope-group header, so suppress the per-leaf
    // badge inside a scope group (avoids "NOW" reading as if it applies to just one field).
    const when = suppressWhen ? null : whenPill(f.when);
    const exclude = f.negate;
    const spectrum = spectrumBadge(f);

    return (
        <span className="relative inline-flex">
            <span
                className={`group inline-flex max-w-full items-center gap-1 rounded-md border px-1.5 py-1 text-[12px] leading-none ${
                    exclude ? 'border-red-200 bg-red-50 text-red-700' : 'border-zinc-200 bg-white text-zinc-700'
                } ${open ? 'ring-2 ring-[#0D1830]/20' : ''}`}
            >
                {when && <span className={`rounded px-1 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide ${when.cls}`}>{when.text}</span>}
                {exclude && <span className="rounded bg-red-100 px-1 py-0.5 text-[9.5px] font-semibold uppercase tracking-wide text-red-700">Not</span>}
                <button
                    type="button"
                    onClick={update ? () => setOpen((o) => !o) : undefined}
                    className={`inline-flex items-center gap-1 ${update ? 'cursor-pointer' : 'cursor-default'}`}
                >
                    {label && <span className="text-[10.5px] font-medium uppercase tracking-wide text-zinc-400">{label}</span>}
                    <span className="truncate font-medium">{value}</span>
                    {spectrum && (
                        <span className={`rounded px-1 py-0.5 text-[9px] font-semibold tracking-wide ${spectrum.cls}`} title={spectrum.tip}>
                            {spectrum.text}
                        </span>
                    )}
                </button>
                {remove && (
                    <button type="button" onClick={() => remove(path)} className="text-zinc-300 opacity-50 hover:text-zinc-700 group-hover:opacity-100" aria-label={`Remove ${label} ${value}`}>
                        <X size={12} />
                    </button>
                )}
            </span>
            {open && update && <LeafEditor f={f} onChange={(next) => update(path, next)} onClose={() => setOpen(false)} />}
        </span>
    );
}

/** A nested group (OR / negated / same-entry scope) rendered as a labeled container so its structure survives. */
function GroupCard({ g, path, remove, update }: { g: Group; path: number[]; remove?: Remove; update?: Update }) {
    const header = g.negate
        ? { text: 'Exclude all of', cls: 'text-red-600' }
        : g.scope === 'experience'
          ? { text: 'In the same role', cls: 'text-[#1C2B4D]' }
          : g.scope === 'education'
            ? { text: 'At the same school', cls: 'text-[#1C2B4D]' }
            : g.combinator === 'or'
              ? { text: 'Any of', cls: 'text-[#1C2B4D]' }
              : { text: 'All of', cls: 'text-zinc-500' };

    // `when` is a property of the whole role/scope — derive one scope-level pill from the group's leaves
    // (past wins, else current) and show it on the header instead of on an arbitrary inner chip.
    const leafWhens = g.scope ? g.children.filter((c): c is Filter => !isGroup(c)).map((c) => c.when) : [];
    const scopeWhen = leafWhens.includes('past') ? 'past' : leafWhens.includes('current') ? 'current' : undefined;
    const scopePill = whenPill(scopeWhen);

    return (
        <div className={`group/card rounded-lg border border-dashed p-2 ${g.negate ? 'border-red-200 bg-red-50/40' : 'border-zinc-300 bg-zinc-50/60'}`}>
            <div className="mb-1.5 flex items-center justify-between">
                <span className="flex items-center gap-1.5">
                    {scopePill && <span className={`rounded px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${scopePill.cls}`}>{scopePill.text}</span>}
                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${header.cls}`}>{header.text}</span>
                </span>
                {remove && (
                    <button type="button" onClick={() => remove(path)} className="text-zinc-300 opacity-50 hover:text-red-500 group-hover/card:opacity-100" aria-label="Remove group">
                        <X size={12} />
                    </button>
                )}
            </div>
            <div className="flex flex-col gap-1.5">
                <NodeList nodes={g.children} combinator={g.combinator} path={path} remove={remove} update={update} suppressWhen={!!g.scope} />
            </div>
        </div>
    );
}

/** Render a list of nodes; insert AND/OR separators so the boolean shape is visible. */
function NodeList({ nodes, combinator, path, remove, update, suppressWhen }: { nodes: QueryNode[]; combinator: 'and' | 'or'; path: number[]; remove?: Remove; update?: Update; suppressWhen?: boolean }) {
    return (
        <>
            {nodes.map((n, i) => (
                <div key={i} className="flex flex-col gap-1.5">
                    {i > 0 && combinator === 'or' && <span className="text-[9.5px] font-bold uppercase tracking-widest text-zinc-300">or</span>}
                    {isGroup(n) ? (
                        <GroupCard g={n} path={[...path, i]} remove={remove} update={update} />
                    ) : (
                        <div className="flex">
                            <LeafChip f={n} path={[...path, i]} remove={remove} update={update} suppressWhen={suppressWhen} />
                        </div>
                    )}
                </div>
            ))}
        </>
    );
}

/**
 * Group the top-level LEAF children by category; nested groups render in their own "Logic" section.
 * When `onChange` is given, each chip/group gets a remove control that mutates the tree by path —
 * the render stays a faithful 1:1 of the tree, so editing can never reintroduce the flattening loss.
 */
export function TreeFilters({ tree, onChange }: { tree: QueryNode; onChange?: (t: Group) => void }) {
    const root: Group = isGroup(tree) ? tree : { type: 'group', combinator: 'and', children: [tree] };
    const remove: Remove | undefined = onChange ? (path) => onChange(removeAtPath(root, path) as Group) : undefined;
    const update: Update | undefined = onChange ? (path, next) => onChange(setAtPath(root, path, next) as Group) : undefined;

    // keep each child's original index in root.children so removal paths stay correct after category grouping
    const leafEntries: { f: Filter; idx: number }[] = [];
    const groupEntries: { g: Group; idx: number }[] = [];
    root.children.forEach((c, idx) => (isGroup(c) ? groupEntries.push({ g: c, idx }) : leafEntries.push({ f: c, idx })));

    const byCat = new Map<string, { f: Filter; idx: number }[]>();
    for (const e of leafEntries) {
        const cat = FIELD_CAT[e.f.field] ?? 'Other';
        if (!byCat.has(cat)) byCat.set(cat, []);
        byCat.get(cat)!.push(e);
    }
    const cats = [...byCat.keys()].sort((a, b) => CAT_ORDER.indexOf(a) - CAT_ORDER.indexOf(b));

    if (!leafEntries.length && !groupEntries.length) {
        return <div className="text-[12.5px] text-zinc-500">No filters yet.</div>;
    }

    return (
        <div className="flex flex-col gap-4">
            {cats.map((cat) => (
                <section key={cat}>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{cat}</div>
                    <div className="flex flex-wrap gap-1.5">
                        {byCat.get(cat)!.map((e) => (
                            <LeafChip key={e.idx} f={e.f} path={[e.idx]} remove={remove} update={update} />
                        ))}
                    </div>
                </section>
            ))}
            {groupEntries.length > 0 && (
                <section>
                    <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Logic</div>
                    <div className="flex flex-col gap-1.5">
                        {groupEntries.map((e) => (
                            <GroupCard key={e.idx} g={e.g} path={[e.idx]} remove={remove} update={update} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
